import { defineStore } from '@dh-io-owpi/shared/src/stores/store';
import { formatLabel, useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';

/**
 * Used to store and handle the logic of video progression tracking according to analytics requirements.
 * It will allow us to track the progress for each video in SPA and persist the state when user switches between sub-brands.
 * It will avoid tracking the same video progress multiple times.
 */
export const useVideoTrackerStore = defineStore<VideoTracker>('spa-videoTracker', () => {
  const videosTrackMap = new Map<string, ReturnType<typeof _videoProgress>>();
  const { track } = useTrackingStore();

  const _videoProgress = () => {
    // Flags to track if events have been fired already
    let reached25 = false;
    let reached50 = false;
    let reached90 = false;
    return ({ event, videoTitle, sectionName, componentName }: VideoTrackerOptions) => {
      const video = event.target as HTMLVideoElement;
      const currentTime = video.currentTime;
      const duration = video.duration;
      const progress = currentTime / duration;
      const videoT = formatLabel(videoTitle);

      if (progress >= 0.25 && !reached25) {
        track({
          name: 'video_progress',
          label: videoT,
          action: `${sectionName}|video_html5.video_25|${componentName}`,
          category: 'video',
        });
        reached25 = true;
      }

      if (progress >= 0.5 && !reached50) {
        track({
          name: 'video_progress',
          label: videoT,
          action: `${sectionName}|video_html5.video_50|${componentName}`,
          category: 'video',
        });
        reached50 = true;
      }

      if (progress >= 0.9 && !reached90) {
        track({
          name: 'video_complete',
          label: videoT,
          action: `${sectionName}|video_html5.video_90|${componentName}`,
          category: 'video',
        });
        reached90 = true;
      }
    };
  };

  return {
    trackVideoProgress(opt) {
      if (!videosTrackMap.has(opt.id)) {
        videosTrackMap.set(opt.id, _videoProgress());
      }
      videosTrackMap.get(opt.id)?.(opt);
    },
  };
});

interface VideoTracker {
  trackVideoProgress(options: VideoTrackerOptions): void;
}

interface VideoTrackerOptions {
  event: Event;
  videoTitle: string;
  sectionName: string;
  componentName: string;
  id: string; // Must be unique for each video
}
